import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWfTyphoonStore } from 'src/api/wf/stores/WfTyphoonStore';
import { useWfProductStore } from 'src/api/wf/stores/WfProductStore';
import useWfProductService from 'src/api/wf/service/WfProductService';
import useWfRecoveryService from 'src/api/wf/service/WfRecoveryService';
import { useWfLocationService } from 'src/api/wf/service/WfLocationService';
import { useAlertStore } from 'src/ui/common/alert/AlertStore';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';
import { FormState, useFormState, usePhotoManager } from './components/hooks';
import Button from '../button/Button';
import InlineAlert from 'src/ui/wf/common/inlineAlert/InlineAlert';
import { AlertMessage } from 'src/ui/common/alert/MultiMessageAlert';
import { FloodRequiredFields } from './components/FloodRequiredFields';
import { PartRequiredFields } from './components/PartRequiredFields';
import { ProductHeader } from './components/ProductHeader';
import { WorkRequestForm } from './components/WorkRequestForm';
import { WfInformationStatusParametersBody } from 'src/api/wf/repository/WfRecoveryRepo';
import {
  WfRecoveryStatus,
  resolveResponseInputForApi,
} from 'src/api/wf/models/enums/WfRecoveryStatus';
import { useNotify } from 'src/ui/common/alert/useNotify';
import { useNotifyMultiMessageAlert } from 'src/ui/common/alert/useNotifyMultiMessageAlert';
export interface PhotoItem {
  id?: string;
  url: string;
  file?: File;
  name: string;
}

function WF_4_6_1_PropertyStatusDetailsRegistration() {
  const { t } = useTranslation();
  const notify = useNotify();
  const notifyAlert = useNotifyMultiMessageAlert();
  const navigate = useNavigate();
  const userAgent = useUserAgent();
  const { showAlert } = useAlertStore();
  const { currentTyphoon } = useWfTyphoonStore();
  const { selectedProduct, productDetails } = useWfProductStore();
  const { fetchProductDetails } = useWfProductService();
  const { updateWfUserLocation } = useWfLocationService();
  const {
    registerWorkplaceInformationStatus,
    uploadFloodPhoto,
    uploadPartsPhoto,
    deleteFloodPhoto,
    deletePartsPhoto,
  } = useWfRecoveryService();

  const { formState, handleFieldChange: updateState, resetForm } = useFormState(productDetails);

  const floodPhotos = usePhotoManager(productDetails?.floodPhotoIds, 'Flood');
  const partsPhotos = usePhotoManager(productDetails?.partsPhotoIds, 'Part');

  const [isRegistering, setIsRegistering] = useState<boolean>(false);

  useEffect(() => {
    if (selectedProduct && currentTyphoon) {
      fetchProductDetails({
        branchCode: selectedProduct.branchCode,
        disasterId: currentTyphoon.id,
        productNumber: selectedProduct.productNumber,
      });
    }
  }, [selectedProduct, currentTyphoon, fetchProductDetails]);

  const handleFieldChange = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      const MAX_LENGTH = 500;

      const textFields: (keyof FormState)[] = ['workRemarks', 'partsComments', 'floodComments'];

      if (textFields.includes(field) && typeof value === 'string') {
        if (value.length >= MAX_LENGTH) {
          notify(t('wf.common.maxLengthReached'));
          const truncatedValue = value.slice(0, MAX_LENGTH) as FormState[K];
          updateState(field, truncatedValue);
          return;
        }
      }

      updateState(field, value);

      if (
        field === 'responseInput' &&
        value !== WfRecoveryStatus.FloodingResponsePartReplacementRequired
      ) {
        floodPhotos.clearAllPhotos();
        partsPhotos.clearAllPhotos();
      }
    },
    [updateState, notify, t, floodPhotos, partsPhotos]
  );

  const handleClearForm = useCallback(() => {
    floodPhotos.resetPhotos();
    partsPhotos.resetPhotos();
    resetForm();
  }, [floodPhotos, partsPhotos, resetForm]);

  const handleRegister = useCallback(async () => {
    if (!currentTyphoon || !productDetails) return;

    if (
      formState.responseInput === WfRecoveryStatus.FloodingResponsePartReplacementRequired &&
      !formState.floodRequired &&
      !formState.partsRequired
    ) {
      showAlert({
        inlineContent: t('wf.statusInformationRegistration.checkboxValidationRequired'),
      });
      return;
    }

    setIsRegistering(true);

    const body: WfInformationStatusParametersBody = {
      disasterId: currentTyphoon.id,
      productNumber: productDetails.productNumber,
      branchCode: productDetails.branchCode,
      ...formState,
      responseInput: resolveResponseInputForApi(
        formState.responseInput,
        formState.floodRequired,
        formState.partsRequired
      ),
    };

    const messages: AlertMessage[] = [];

    try {
      // Step 1: Register recovery info
      try {
        await registerWorkplaceInformationStatus(body);
        messages.push({
          messageKey: 'wf.common.recoveryInfoRegistrationSuccess',
          isError: false,
        });
      } catch {
        messages.push({
          messageKey: 'wf.common.recoveryInfoRegistrationError',
          isError: true,
        });
      }

      // Step 2: Upload photos
      const uploadTasks: { type: 'flood' | 'parts'; promise: Promise<string> }[] = [];
      floodPhotos.photoRows.flat().forEach((photo) => {
        if (photo.file) {
          uploadTasks.push({
            type: 'flood',
            promise: uploadFloodPhoto({ ...body, photoData: photo.file, fileName: photo.name }),
          });
        }
      });
      partsPhotos.photoRows.flat().forEach((photo) => {
        if (photo.file) {
          uploadTasks.push({
            type: 'parts',
            promise: uploadPartsPhoto({ ...body, photoData: photo.file, fileName: photo.name }),
          });
        }
      });

      if (uploadTasks.length > 0) {
        const uploadResults = await Promise.allSettled(uploadTasks.map((task) => task.promise));
        const failedUploadCount = uploadResults.filter(
          (result) => result.status === 'rejected'
        ).length;

        if (failedUploadCount > 0) {
          messages.push({
            messageKey: 'wf.common.photoRegistrationError',
            isError: true,
          });
        } else {
          messages.push({
            messageKey: 'wf.common.photoRegistrationSuccess',
            isError: false,
          });
        }
      }

      // Step 3: Delete photos
      const deletionPromises: Promise<boolean>[] = [];
      floodPhotos.pendingDeletionPhotoIds.forEach((photoId) => {
        deletionPromises.push(deleteFloodPhoto(photoId));
      });
      partsPhotos.pendingDeletionPhotoIds.forEach((photoId) => {
        deletionPromises.push(deletePartsPhoto(photoId));
      });

      if (deletionPromises.length > 0) {
        const deletionResults = await Promise.allSettled(deletionPromises);
        const hasDeletionFailure = deletionResults.some(
          (result) =>
            result.status === 'rejected' || (result.status === 'fulfilled' && !result.value)
        );
        messages.push({
          messageKey: hasDeletionFailure
            ? 'wf.common.photoDeletionError'
            : 'wf.common.photoDeletionSuccess',
          isError: hasDeletionFailure,
        });
      }

      floodPhotos.clearPendingDeletionPhotoIds();
      partsPhotos.clearPendingDeletionPhotoIds();

      if (userAgent.isMobile) {
        updateWfUserLocation();
      }

      notifyAlert({
        messages,
        onCloseCallback: () => navigate(-1),
      });
    } catch (error) {
      console.error('Information registration failed:', error);
      showAlert({ inlineContent: t('wf.common.failedInformationRegistration') });
      setIsRegistering(false);
    }
  }, [
    formState,
    currentTyphoon,
    productDetails,
    floodPhotos,
    partsPhotos,
    registerWorkplaceInformationStatus,
    uploadFloodPhoto,
    uploadPartsPhoto,
    deleteFloodPhoto,
    deletePartsPhoto,
    userAgent,
    updateWfUserLocation,
    notifyAlert,
    navigate,
    showAlert,
    t,
  ]);

  return (
    <div className="h-fit max-h-screen space-y-3 overflow-y-auto p-6">
      <InlineAlert />
      <ProductHeader product={productDetails} />

      <WorkRequestForm formState={formState} onFieldChange={handleFieldChange} />

      {/* Flood Section */}
      {formState.responseInput === WfRecoveryStatus.FloodingResponsePartReplacementRequired && (
        <label className="flex items-center gap-2">
          <span className="w-60 bg-blue-500 font-semibold">
            {t('wf.statusInformationRegistration.floodRequired')}
          </span>
          <input
            type="checkbox"
            checked={formState.floodRequired}
            onChange={(e) => handleFieldChange('floodRequired', e.target.checked)}
          />
        </label>
      )}
      {formState.floodRequired && (
        <FloodRequiredFields
          formState={formState}
          onFieldChange={handleFieldChange}
          photoManager={floodPhotos}
        />
      )}

      {/* Parts Section */}
      {formState.responseInput === WfRecoveryStatus.FloodingResponsePartReplacementRequired && (
        <label className="flex items-center gap-2">
          <span className="w-60 bg-green-500 font-semibold">
            {t('wf.statusInformationRegistration.partNeeded')}
          </span>
          <input
            type="checkbox"
            checked={formState.partsRequired}
            onChange={(e) => handleFieldChange('partsRequired', e.target.checked)}
          />
        </label>
      )}
      {formState.partsRequired && (
        <PartRequiredFields
          formState={formState}
          onFieldChange={handleFieldChange}
          photoManager={partsPhotos}
        />
      )}

      <div className="flex gap-3">
        <Button title={t('wf.common.register')} onClick={handleRegister} disabled={isRegistering} />
        <Button title={t('wf.common.clear')} onClick={handleClearForm} disabled={isRegistering} />
        <Button title={t('wf.common.back')} onClick={() => navigate(-1)} disabled={isRegistering} />
      </div>
    </div>
  );
}

export default WF_4_6_1_PropertyStatusDetailsRegistration;
